import React, { useState } from 'react';
import { Button } from '@getro/rombo';
import { ChevronLeft, Trash } from 'lucide-react';
import { Box, Flex, Text } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { trackEvent } from 'helpers/analytics';
import { useUpdateCustomFieldMutation, useDeleteCustomFieldMutation } from 'services/customFieldsV2';
import { CustomFieldForm } from './customFieldForm';
import { DeleteCustomFieldModal } from './deleteCustomFieldModal';

export const EditCustomField = ({ customField, selectedColumns, setSelectedColumns, onBack, page }) => {
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [updateCustomField, { isLoading: isLoadingUpdate }] = useUpdateCustomFieldMutation();
  const [deleteCustomField, { isLoading: isLoadingDelete }] = useDeleteCustomFieldMutation();
  const { listId } = useParams();

  const handleSubmit = (values) => {
    updateCustomField({
      id: customField.id,
      name: values.name,
      type: values.type.value,
      scope: values.scope.value,
      ...(values.type.value === 'single_select' ? { params: { options: values.options } } : {}),
      ...(listId ? { sharedListId: listId } : undefined),
    })
      .unwrap()
      .then((response) => {
        let newColumn = null;
        setSelectedColumns(
          selectedColumns.map((column) => {
            if (column.customField?.id === customField.id) {
              const result = { ...column, Header: response.name, customField: response };
              newColumn = { ...column, Header: response.name, customField: response };
              return result;
            }

            return column;
          }),
          'edit_custom_field',
          newColumn,
        );

        trackEvent('contacts:custom_field:edited', {
          listId,
          type: values.type.value,
          customFieldId: response.id,
        });

        onBack();
      })
      .catch(() => {
        toast('Failed to update custom field', { icon: 'error', id: 'update-custom-field' });
      });
  };

  const handleDelete = (e) => {
    deleteCustomField({ id: customField.id, ...(listId ? { sharedListId: listId } : undefined) })
      .unwrap()
      .then(() => {
        setSelectedColumns(
          selectedColumns.filter((column) => column.customField?.id !== customField.id),
          'delete_custom_field',
          customField,
        );
        trackEvent('contacts:custom_field:deleted', {
          listId,
          type: customField.type,
          customFieldId: customField.id,
        });

        onBack(e);
      })
      .catch(() => {
        toast('Failed to delete custom field', { icon: 'error', id: 'delete-custom-field' });
      });
  };

  return (
    <Box
      sx={{
        p: '16px',
        width: '320px',
        boxShadow: '0px 2px 32px 0px #1B24320F, 0px 1px 4px 0px #1B24320F',
        position: 'absolute',
        left: ['-100px', '-100px', '0'],
        top: '30px',
        zIndex: 11000,
        backgroundColor: 'white',
      }}
    >
      <Flex alignItems="center" sx={{ gap: '8px' }}>
        <Box
          as={ChevronLeft}
          width="16px"
          height="16px"
          aria-hidden="true"
          onClick={onBack}
          sx={{ cursor: 'pointer', color: 'neutral.500', '&:hover': { color: 'neutral.700' } }}
        />
        <Text fontSize="14px" fontWeight="medium">
          Edit custom field
        </Text>
      </Flex>
      <CustomFieldForm
        customField={customField}
        isLoading={isLoadingUpdate}
        onSubmit={handleSubmit}
        onCancel={onBack}
        page={page}
      />
      <Flex
        mt="16px"
        pt="16px"
        alignItems="center"
        sx={{ gap: '8px', borderTop: '1px solid', borderColor: 'border.subtle' }}
      >
        <Button
          icon={Trash}
          variant="tertiary"
          size="small"
          sx={{ color: 'red.600', px: '0px', fontWeight: 'body' }}
          loading={isLoadingDelete}
          onClick={() => setIsOpenDeleteModal(true)}
        >
          Delete field
        </Button>
      </Flex>
      {isOpenDeleteModal && (
        <DeleteCustomFieldModal
          onCloseModal={() => setIsOpenDeleteModal(false)}
          onDeleteCustomField={handleDelete}
          isLoading={isLoadingDelete}
        />
      )}
    </Box>
  );
};

EditCustomField.propTypes = {
  customField: PropTypes.object.isRequired,
  selectedColumns: PropTypes.array.isRequired,
  setSelectedColumns: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  page: PropTypes.string,
};

EditCustomField.defaultProps = {
  page: null,
};
